import { create } from 'zustand';

import i18n from '@/i18n';
import { services } from '@/services';
import { toAppLocale, type AppLocale, type Catalog } from '@/types';

/**
 * Catalog Store — P3 node catalog session cache(對齊 docs/44 FE 整合契約)
 * -----------------------------------------------------------------------------
 * catalog 很穩定 → canvas mount 拉一次、cache 整個 session。所有 TOOL 動態表單 /
 * model 下拉 / 估價都從這份讀,**不持久化**(每次 app 啟動重 fetch — 後端改 model /
 * 改價 / 加參數立即生效,不被 localStorage stale 卡住)。
 *
 * 三態 status:
 *   - `idle`:還沒拉過。
 *   - `loading`:`GET /workflow/catalog` in-flight。
 *   - `ready`:`catalog` 有值。
 *   - `error`:拉失敗(`error` 帶訊息)。Build phase 對「未載入」應優雅降級
 *     (docs/44 §8:catalog doc 壞時後端 fail-open 回 code 預設,幾乎不回 5xx;
 *     真失敗時 caller 可 fallback 既有寫死表)。
 *
 * `load()` **dedupe**:status 已是 `loading` 或 `ready` 直接 return,避免 canvas
 * 多 mount / StrictMode double-invoke 重複打 endpoint。要強制重拉(submit 撞
 * `NODE_CONFIG_INVALID` 後重拉)先 `clear()` 再 `load()`。
 *
 * `getCatalog()` imperative getter 給純函式 / 非 React context 讀(對齊
 * `groupContextStore.getCurrentGroupId` 慣例)。
 *
 * ## R426(BE docs/139)— catalog 帶介面語系抓
 * 範本 / 模特兒的顯示字(`title` / `tags` / reel・文案 `prompt` / 模特兒 `label` / `category_label`)
 * 跟著 `?locale=` 回,所以這份 cache **是某一個語系的**(`locale` 欄)。三條判準:
 *   1. **語系 = 畫面實際顯示的語言**(`i18n.resolvedLanguage`,同 R424 `outputLocale` 第 1 條):
 *      en / ja 字典載入失敗退回繁中時,範本名稱也要是繁中,不然同一個畫面兩種語言。
 *   2. **切語系就重抓**(模組底部的 `languageChanged` 訂閱),而且**舊的那份照常顯示到新的回來**:
 *      工具 / 模型 / `features` 不分語系,清掉 = 切換當下整片依 catalog 長出來的入口閃成「未載入」。
 *      重抓失敗 → 留著舊的(只是範本名稱停在舊語系,下次 `load()` 會再試)。
 *   3. **晚到的舊語系回應不可以蓋掉新語系**(快速連切 zh → en → ja):回應落地時語系已經不是它,
 *      而手上已經有一份 → 丟掉。
 * 卡上只存 `template_id` / `character_id`(名稱一律由這份 catalog 以 id 反查),所以換一份 catalog
 * 卡面名稱就跟著換;已預填進 brief 的文字是使用者內容,**這裡不碰**(docs/139 §4)。
 */
export type CatalogStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface CatalogState {
  catalog: Catalog | null;
  /**
   * R426 — 手上這份 `catalog` 是用哪個語系抓的。`null` = 還沒有,或是外部直接塞進來的(測試
   * `setState`)—— 沒有語系標記的那份**視同當下語系**,不因為語系不明就重抓。
   */
  locale: AppLocale | null;
  status: CatalogStatus;
  error: string | null;
  /**
   * 拉「目前介面語系」那份 catalog 進 cache。dedupe:手上那份(或在飛那發)已經是這個語系 → no-op。
   * 首次失敗 → status `error` + `error` 訊息(不 throw,讓 caller useEffect 不需 catch)。
   */
  load: () => Promise<void>;
  /** 清回 idle(強制重拉前用,或 onAuthLost 切 user 時清掉前一個 user 的 catalog)。 */
  clear: () => void;
  /**
   * 強制重拉(submit 撞 `NODE_CONFIG_INVALID` 後 client catalog 可能過期 → 重拉,
   * docs/44 §1.5)。= `clear()` 再 `load()`;不能只 `clear()`(只回 idle,要靠
   * component re-mount 才重拉,submit catch 場景沒有 re-mount)。
   */
  invalidate: () => Promise<void>;
  /**
   * R424.1 — 「我現在就要讀 catalog」:ready → 直接回;**載入中 → 等那一發載完**;idle / error →
   * 觸發載入並等它。回 catalog(失敗 = `null`,不 throw)。
   *
   * 為什麼不能直接 `await load()`:`load()` 遇到 `loading` 會**立刻 return**(dedupe),不等那發
   * 在飛的請求 —— 畫布以外的頁面(素材庫)剛好在別處觸發載入的空檔按下,就會讀到 `null`。
   */
  ensureLoaded: () => Promise<Catalog | null>;
}

const initialState = {
  catalog: null as Catalog | null,
  locale: null as AppLocale | null,
  status: 'idle' as CatalogStatus,
  error: null as string | null,
};

/** 在飛中的那一發載入(`ensureLoaded` 要等它;`load()` 的 dedupe 不等)。 */
let inflight: Promise<void> | null = null;
/** 在飛那一發抓的語系(換語系時 dedupe 要比對它,不是只看「有沒有在飛」)。 */
let inflightLocale: AppLocale | null = null;

/** 判準 1:畫面實際顯示的語言(字典沒載到而退回繁中時就是繁中)。 */
function displayLocale(): AppLocale {
  return toAppLocale(i18n.resolvedLanguage);
}

export const useCatalogStore = create<CatalogState>((set, get) => ({
  ...initialState,
  load: async () => {
    // dedupe:手上 / 在飛那份已經是這個語系就不重打。error 狀態允許重試(由 clear 後再 load,
    // 或直接 load — error 不擋,讓 canvas re-mount 自動重試一次)。
    const want = displayLocale();
    const { status, locale } = get();
    if (status === 'ready' && (locale === null || locale === want)) return;
    if (status === 'loading' && (inflightLocale === null || inflightLocale === want)) return;

    // 判準 2:換語系重抓時舊那份照常顯示(status 維持 ready);只有第一次載入才進 loading。
    if (get().catalog === null) set({ status: 'loading', error: null });
    const run = (async () => {
      try {
        const catalog = await services.catalogService.get({ locale: want });
        // 判準 3:晚到的舊語系回應不蓋掉手上那份(手上還沒有任何一份時照收 —— 有總比沒有好,
        // 新語系那發回來會再換掉)。
        if (want !== displayLocale() && get().catalog !== null) return;
        set({ catalog, locale: want, status: 'ready', error: null });
      } catch (e) {
        // 換語系重抓失敗:舊那份還能用(工具 / 模型不分語系),不要把整份 catalog 翻成 error。
        if (get().catalog !== null) return;
        const message = e instanceof Error ? e.message : String(e);
        set({ status: 'error', error: message });
      }
    })();
    inflight = run;
    inflightLocale = want;
    try {
      await run;
    } finally {
      if (inflight === run) {
        inflight = null;
        inflightLocale = null;
      }
    }
  },
  ensureLoaded: async () => {
    const { status } = get();
    if (status === 'ready') return get().catalog;
    if (status === 'loading' && inflight) await inflight;
    else await get().load();
    return get().catalog;
  },
  clear: () => set(initialState),
  invalidate: async () => {
    // 先清回 idle(否則 load 的 ready dedupe 會 no-op),再重拉一次 fresh catalog。
    set(initialState);
    await get().load();
  },
}));

/**
 * Imperative getter for 純函式 / 非 React context。
 *
 * catalogValidate 那層是純函式 + 由 caller 注入 `cap`,本身不讀 store;但 Build
 * phase 端若需在事件 handler(非 hook)裡讀整份 catalog,走這個 getter。
 */
export function getCatalog(): Catalog | null {
  return useCatalogStore.getState().catalog;
}

/**
 * R426 判準 2 — 介面語系一換就重抓(docs/139 §4.1)。只重抓「依語系抓回來的那份」:還沒載過
 * (idle,例如登入頁)不為了切語言多打一發;外部塞進來、沒有語系標記的那份也不動(見 `locale`)。
 * 首次載入還在飛時切語言 → `load()` 會為新語系另開一發(在飛那發的語系不一樣)。
 */
i18n.on('languageChanged', () => {
  if (useCatalogStore.getState().locale === null && inflight === null) return;
  void useCatalogStore.getState().load();
});
